import { defineStore } from "pinia";
import client from "../httpClient/client";
import {
  CalculatorNotePayload,
  CalculatorNoteResponse,
  RootState,
} from "types";
import { LocationQueryValue } from "vue-router";
import { getCompletedBy } from "@/composables/booking/completedBy";
import {
  hasAnyBrokerCtaSignal,
  isBrokerCtaHoldActiveFromInputs,
} from "@/utils/brokerCtaHold";
import { prefetchInsurerLogos } from "@/utils/logoUrl";

const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
const basePath = isLocalEnv ? process.env.VUE_APP_TARIFF_MANAGER_URL : "/";

export const useRootStore = defineStore("root", {
  state: (): RootState => ({
    appName: "",
    appAlias: "",
    theme: "",
    activeUser: "",
    participantToken: "",
    compareDifferences: false,
    compareShowInlineTooltips: false,
    compareShowPaymentOptions: false,
    affiliateData: {},
    progressBarSteps: null,
    activeStep: 0,
    completedSteps: 0,
    hasAParameter: false,
    hasRParameter: false,
    fromPortal: false,
    accessLevel: "",
    portalAffiliateId: "",
    affiliateInfo: {},
    browser: "",
    openFromSearch: false,
    brokerStatusAccepted: false,
    urlProductType: "",
    toPreviousPage: false,
    toStep: 0,
    toNextPage: false,
    fromWizard: false,
    searchFlowOrigin: null,
    fromDetails: false,
    fromAssessment: false,
    modifiedSearch: false,
    missingValue: false,
    redirectBooking: false,
    bookingId: "",
    modalOpen: false,
    preview: "",
    origin: "",
    scrollTo: "",
    showEditOverlay: false,
    tariffForLinkout: {},
    fetchedDocuments: false,
    tooltipOpen: false,
    paymentNotSelected: false,
    paymentError: false,
    atSearch: true,
    hasHeader: true,
    hasFooter: true,
    hasBookingFooter: false,
    code: "",
    language: "de",
    completedBy: "end_customer",
    manuallyEditedFields: [],
    requestingSharingUpdates: false,
    renewalModelOpen: false,
    renewalBirthdate: "",
    renewalPostalCode: "",
    showLegalLinks: "true",
    impressumLink: "https://www.covomo.de/impressum/",
    contactLink: "https://www.covomo.de/kontakt/",
    dataProtectionLink: "https://www.covomo.de/datenschutz/",
    erstinformationLink: "https://cdn.covomo.de/common/erstinformation.pdf",
    agbLink: "https://www.covomo.de/agb/",
    priceToken: "",
    isNavigatingToNewPage: false,
    spinnerLogos: [],
    enableSharing: "true",
    enableSaving: "true",
    enableSharingWithBroker: "true",
    enableDocumentsZipDownload: true,
    enableComparison: "true",
    enableBasisberatung: "true",
    resourceFieldChanged: false,
    fromComparison: false,
    brokerEmail: "",
    poolEmail: "",
    poolName: "",
    brokerId: "",
    brokerSubId: "",
    brokerName: "",
    brokerPhone: "",
    brokerCompany: "",
    brokerInfovisible: "",
    brokerInfoPrefill: "",
    isAssessmentLink: false,
    isQuoteRequest: false,
    transactionCode: "",
    showTransactionCode: false,
    focusSealsTariffId: "",
    initialPrice: "",
    finalPrice: "",
    oldPriceToShow: "",
    priceModal: false,
    priceChangeAccepted: true,
    priceChangeDeclined: false,
    sessionPrerequest: {},
    manualAddBtnClick: false,
    tariffUnavailable: false,
    unavailableTariffMessage: "",
    tariffManagerSearchResultsError: "",
    resumingBooking: false,
    additionalBooking: false,
    isContractModelChanged: false,
    globalSpinnerActive: false,
    cancelContractLink: "https://www.covomo.de/vertrag-widerrufen/",
    appTitle: "",
  }),
  getters: {
    brokerCtaHoldActive(): boolean {
      return isBrokerCtaHoldActiveFromInputs();
    },
    brokerCtaDisabled(): boolean {
      if (!this.brokerCtaHoldActive) return false;
      return !hasAnyBrokerCtaSignal({
        brokerPhone: this.brokerPhone,
        brokerName: this.brokerName,
        brokerCompany: this.brokerCompany,
        brokerEmail: this.brokerEmail,
        poolEmail: this.poolEmail,
        poolName: this.poolName,
        brokerId: this.brokerId,
        brokerSubId: this.brokerSubId,
      });
    },
  },
  actions: {
    async getCalculatorNote(
      data: CalculatorNotePayload,
    ): Promise<CalculatorNoteResponse> {
      return client.getRequest(
        `${basePath}api/v1/notes/?application=${data.calculatorName}&white_label=${data.theme}`,
      );
    },
    async fetchAffiliateInfo(
      affiliateId: LocationQueryValue | LocationQueryValue[],
    ) {
      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const response = await client.getRequest(
        `${basePath}api/v1/brokers/${affiliateId}`,
      );
      const incoming = response.data.data;
      this.affiliateInfo = incoming;
      const current = this.affiliateData || {};
      const inc = incoming || {};

      const merged: Record<string, unknown> = { ...current };

      for (const [key, value] of Object.entries(inc)) {
        const isEmpty =
          value === "" ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0);

        if (!isEmpty) {
          merged[key] = value;
        }
      }

      this.affiliateData = merged;
      return response;
    },
    isFieldManuallyEdited(fieldKey: string): boolean {
      return this.manuallyEditedFields.includes(fieldKey);
    },
    async sendTokenByEmail(payload) {
      return Promise.resolve({
        data: {
          success: true,
        },
      });

      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const url = `${basePath}api/v1/emails`;
      return client.postRequest(url, payload);
    },
    async validateToken(payload) {
      return Promise.resolve({
        data: {
          success: true,
        },
      });

      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const url = `${basePath}api/v1/emails`;
      return client.postRequest(url, payload);
    },
    addManuallyEditedField(fieldKey: string) {
      if (!this.manuallyEditedFields.includes(fieldKey)) {
        this.manuallyEditedFields.push(fieldKey);
      }
    },
    setGlobalSpinnerActive(val) {
      this.globalSpinnerActive = val;
    },
    setpriceChangeAccepted(val) {
      this.priceChangeAccepted = val;
    },
    setCancelContractLink(val) {
      this.cancelContractLink = val;
    },
    setpriceChangeDeclined(val) {
      this.priceChangeDeclined = val;
    },
    setTransactionCodeVisibility(val) {
      this.showTransactionCode = val;
    },
    setIsNavigatingToNewPage(value) {
      this.isNavigatingToNewPage = value;
    },
    setUser(value) {
      this.activeUser = value;
    },
    setIsContractModelChanged(val) {
      this.isContractModelChanged = val;
    },
    setAdditionalBooking(val) {
      this.additionalBooking = val;
    },
    setPriceModal(val) {
      this.priceModal = val;
    },
    setRewalBirthdate(date) {
      this.renewalBirthdate = date;
    },
    setEnableSharing(value) {
      this.enableSharing = value;
    },
    setEnableSaving(value) {
      this.enableSaving = value;
    },
    setEnableSharingWithBroker(value) {
      this.enableSharingWithBroker = value;
    },
    setEnableDocumentsZipDownload(value: boolean) {
      this.enableDocumentsZipDownload = value;
    },
    setUnavailableTariffMessage(val) {
      this.unavailableTariffMessage = val;
    },
    setTariffManagerSearchResultsError(val: string) {
      this.tariffManagerSearchResultsError = val;
    },
    setTariffUnavailable(val) {
      this.tariffUnavailable = val;
    },
    setFromComparison(val) {
      this.fromComparison = val;
    },
    setisAssessmentLink(val) {
      this.isAssessmentLink = val;
    },
    setInitialPrice(val) {
      this.initialPrice = val;
    },
    setFinalPrice(val) {
      this.finalPrice = val;
    },
    setAppTitle(val) {
      this.appTitle = val;
    },
    setManualAddBtnClick(val) {
      this.manualAddBtnClick = val;
    },
    setIsQuoteRequest(val) {
      this.isQuoteRequest = val;
    },
    setEnableComparison(value) {
      this.enableComparison = value;
    },
    setEnableBasisberatung(value) {
      this.enableBasisberatung = value;
    },
    setPriceToken(value) {
      this.priceToken = value;
    },
    setRenewalPostalCode(value) {
      this.renewalPostalCode = value;
    },
    setSpinnerLogos(logos) {
      this.spinnerLogos = logos;
      // Prefetch early with low concurrency — same host as tariffs XHR locally.
      prefetchInsurerLogos(logos);
    },
    setAppName(name: string) {
      this.appName = name;
    },
    setAppAlias(name: string) {
      this.appAlias = name;
    },
    setUrlProductType(value: string) {
      this.urlProductType = value;
    },
    setCode(value: RootState["code"]) {
      this.code = value;
    },
    setLanguage(value: unknown) {
      const raw = Array.isArray(value) ? value[value.length - 1] : value;
      if (raw === undefined || raw === null) {
        this.language = "";
        return;
      }
      this.language = String(raw).toLowerCase().trim() || "";
    },
    setCompletedBy() {
      this.completedBy = getCompletedBy(
        this.affiliateData,
        this.accessLevel,
        this.fromPortal,
        this.portalAffiliateId,
      );
    },
    setBrokerStatusAccepted(value: (value: unknown) => void) {
      this.setBrokerStatusAccepted = value;
    },
    setTheme(theme: string) {
      this.theme = theme;
    },
    setImpressumLink(link) {
      this.impressumLink = link;
    },
    setContactLink(link) {
      this.contactLink = link;
    },
    setTooltipOpen(data: boolean) {
      this.tooltipOpen = data;
    },
    setAffiliateData(data: Record<string, unknown>) {
      /**
       * Legacy `r` param uses key `a`; newer flows use `affiliate_id`.
       */
      const patch: Record<string, unknown> = { ...(data || {}) };
      const idMissing =
        patch.affiliate_id === undefined ||
        patch.affiliate_id === null ||
        patch.affiliate_id === "";
      const aVal = patch.a;
      const aPresent =
        aVal !== undefined &&
        aVal !== null &&
        aVal !== "" &&
        !(Array.isArray(aVal) && aVal.length === 0);
      if (idMissing && aPresent) {
        patch.affiliate_id = Array.isArray(aVal) ? aVal[aVal.length - 1] : aVal;
      }
      this.affiliateData = {
        ...(this.affiliateData || {}),
        ...patch,
      };
      this.setCompletedBy();
    },
    setResourceFieldChanged(val: boolean) {
      this.resourceFieldChanged = val;
    },
    setFocusSeals(val) {
      this.focusSealsTariffId = val;
    },
    setOrigin(value) {
      this.origin = value;
    },
    setSessionPrerequest(val) {
      this.sessionPrerequest = val;
    },
    setRenewalModalOpen(val) {
      this.renewalModelOpen = val;
    },
    setPreview(data) {
      this.preview = data;
    },
    setTransactionCode(token) {
      this.transactionCode = token;
    },
    setModifiedSearch() {
      this.modifiedSearch = true;
    },
    setResumingBooking(val) {
      this.resumingBooking = val;
    },
    setToPreviousPage(data: boolean) {
      this.toPreviousPage = data;
    },
    setProgressBarSteps(data: RootState["progressBarSteps"]) {
      this.progressBarSteps = data;
    },
    setCompletedSteps(data: number) {
      this.completedSteps = data;
    },
    setRedirectBooking(data) {
      this.redirectBooking = data;
    },
    setActiveStep(data: number) {
      this.activeStep = data;
    },
    setToStep(data: number) {
      this.toStep = data;
    },
    setRParameter(data: boolean) {
      this.hasRParameter = data;
      this.setCompletedBy();
    },
    setAParameter(data: boolean) {
      this.hasAParameter = data;
      this.setCompletedBy();
    },
    setFromPortal(data: boolean) {
      this.fromPortal = data;
      this.setCompletedBy();
    },
    setShowLegalLinks(data) {
      this.showLegalLinks = data.toString();
    },
    setBrowser(agent: string) {
      this.browser = agent;
    },
    setMissingValue(data) {
      this.missingValue = data;
    },
    setBrokerEmail(email) {
      this.brokerEmail = email;
    },
    setPoolEmail(email) {
      this.poolEmail = email;
    },
    setPoolName(name: string) {
      this.poolName = name;
    },
    setBrokerId(id: string) {
      this.brokerId = id;
    },
    setBrokerSubId(id: string) {
      this.brokerSubId = id;
    },
    setBrokerName(name) {
      this.brokerName = name;
    },
    setBrokerPhone(phone) {
      this.brokerPhone = phone;
    },
    setBrokerCompany(company: string) {
      this.brokerCompany = company;
    },
    setBookingId(id: string) {
      this.bookingId = id;
    },
    setBrokerInfoPrefill(val) {
      this.brokerInfoPrefill = val;
    },
    setBrokerInfoVisible(val) {
      this.brokerInfovisible = val;
    },
    setPaymentNotSelected(val) {
      this.paymentNotSelected = val;
    },
    setPaymentError(val) {
      this.paymentError = val;
    },
    setAtSearch(val) {
      this.atSearch = val;
    },
    setModalOpen(data) {
      this.modalOpen = data;
    },
    setAccessLevel(accessLevel: string) {
      this.accessLevel = accessLevel;
      this.setCompletedBy();
    },
    setPortalAffiliateId(affiliateId: string) {
      this.portalAffiliateId = String(affiliateId || "").trim();
      this.setCompletedBy();
    },
  },
});
